import { useEffect, useMemo, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faChevronDown, faPlus } from '@fortawesome/free-solid-svg-icons';
import type { CustomerLocation } from '@/services/customer.service';
import type { AddressSelection } from '@/services/equipment-intake.service';
import { Input } from '@/components/ui/input';
import { ADDRESS_SIDES, type AddressSide } from './address-side';
import { iconFor, matchesQuery, optionIdFor, type AddressOption } from './address-labels';

/**
 * Searchable place picker for one side.
 *
 * ARCHITECTURE — unchanged from the radio version it replaces:
 * this emits a SELECTION ({ source, locationId }), never an address. The server
 * resolves it and freezes the snapshot. A client that could post address text
 * could post a GSTIN that was never the customer's, and these values are printed
 * on invoices.
 */
export function AddressPicker({
  side,
  value,
  onChange,
  options,
  locationsById,
  onAddAddress,
  disabled = false,
}: {
  side: AddressSide;
  value: AddressSelection;
  onChange: (next: AddressSelection) => void;
  options: AddressOption[];
  /** Backing rows, so search can also match city and street. */
  locationsById: Map<string, CustomerLocation>;
  onAddAddress: () => void;
  disabled?: boolean;
}) {
  const s = ADDRESS_SIDES[side];
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const selectedId = optionIdFor(value);
  const selected = options.find((o) => o.id === selectedId) ?? null;

  const filtered = useMemo(
    () => options.filter((o) => matchesQuery(o, locationsById.get(o.id) ?? null, query)),
    [options, locationsById, query],
  );

  // Close on outside click. Registered only while open so every picker on the
  // page isn't listening to every document click.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  useEffect(() => {
    if (open) {
      setQuery('');
      setActive(
        Math.max(
          0,
          filtered.findIndex((o) => o.id === selectedId),
        ),
      );
      searchRef.current?.focus();
    }
    // filtered/selectedId intentionally omitted: this seeds state on open only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const choose = (option: AddressOption) => {
    onChange(option.selection);
    setOpen(false);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) =>
        Math.min(filtered.length - 1, Math.max(0, i + (e.key === 'ArrowDown' ? 1 : -1))),
      );
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const opt = filtered[active];
      if (opt) choose(opt);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
    }
  };

  const listboxId = `addr-list-${side.replace(/\W/g, '')}`;

  return (
    <div className="flex flex-col">
      <div className={`flex items-center gap-2 text-[15px] font-semibold ${s.text}`}>
        <FontAwesomeIcon icon={s.icon} className="h-4 w-4" />
        {side}
      </div>
      <p className="mb-2 mt-0.5 text-[13px] text-muted-foreground">{s.purpose}</p>

      <div className="relative" ref={rootRef}>
        <button
          type="button"
          disabled={disabled}
          onClick={() => setOpen((o) => !o)}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={open ? listboxId : undefined}
          className={`flex min-h-[46px] w-full items-center gap-2 rounded-lg border border-border bg-background px-3 py-2 text-left text-sm ${s.hoverBorder} disabled:cursor-not-allowed disabled:opacity-60`}
        >
          {selected && (
            <FontAwesomeIcon icon={iconFor(selected)} className={`h-4 w-4 shrink-0 ${s.text}`} />
          )}
          <span className="flex-1 truncate font-semibold">
            {selected ? selected.name : 'Select an address'}
          </span>
          <FontAwesomeIcon
            icon={faChevronDown}
            className="h-3 w-3 shrink-0 text-muted-foreground"
          />
        </button>

        {open && (
          <div className="absolute left-0 right-0 top-[calc(100%+4px)] z-30 rounded-lg border border-border bg-card shadow-lg">
            <div className="border-b border-border p-2">
              <Input
                ref={searchRef}
                value={query}
                placeholder="Search addresses…"
                onChange={(e) => {
                  setQuery(e.target.value);
                  setActive(0);
                }}
                onKeyDown={onKeyDown}
              />
            </div>
            <ul id={listboxId} role="listbox" className="max-h-64 overflow-y-auto p-1">
              {filtered.length === 0 ? (
                <li className="px-3 py-3 text-center text-[13px] text-muted-foreground">
                  {options.length === 0
                    ? 'No addresses on file for this customer yet.'
                    : 'No address matches that search.'}
                </li>
              ) : (
                filtered.map((o, i) => (
                  <li key={o.id} role="option" aria-selected={o.id === selectedId}>
                    <button
                      type="button"
                      onMouseEnter={() => setActive(i)}
                      onClick={() => choose(o)}
                      className={`flex min-h-[42px] w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm ${
                        i === active ? 'bg-muted' : ''
                      }`}
                    >
                      <FontAwesomeIcon icon={iconFor(o)} className={`h-4 w-4 shrink-0 ${s.text}`} />
                      <span className="flex-1 truncate">{o.name}</span>
                      {o.id === selectedId && (
                        <FontAwesomeIcon icon={faCheck} className={`h-3 w-3 shrink-0 ${s.text}`} />
                      )}
                    </button>
                  </li>
                ))
              )}
            </ul>
          </div>
        )}
      </div>

      <button
        type="button"
        onClick={onAddAddress}
        disabled={disabled}
        className="mt-1.5 inline-flex min-h-[46px] w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 text-sm font-semibold text-primary-foreground transition-[filter] hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-60"
      >
        <FontAwesomeIcon icon={faPlus} className="h-3.5 w-3.5" />
        Add Address
      </button>
    </div>
  );
}
